import React, { useContext, useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import { FormikProvider, FormikValues, useFormik } from 'formik'
import { StepInviteRecorderContext } from './Context'
import { schema, schemaStep4 } from './schema'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { SimpleSnackbarContext } from '../SimpleSnackbar'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Step4 from './steps/step4'
import Step2 from './steps/step2'
import Step3 from './steps/step3'
import Step1 from './steps/step1'
import { CollectionType, EmailRecords, InviteType, MediaType, StoryType } from '../../lib/types'
import { addDays } from 'date-fns'
import api from '../../lib/api'
import LoadingButton from '@mui/lab/LoadingButton'
import { useMitt } from 'react-mitt'
import { UserContext } from '../UserProvider'

const StepInviteRecorder: React.FC<{ initialCollection: CollectionType | null; initialStory: StoryType | null }> = ({
  initialCollection,
  initialStory
}) => {
  const { user } = useContext(UserContext)
  const { actions } = useContext(StepInviteRecorderContext)
  const { actions: snackActions } = useContext(SimpleSnackbarContext)
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>('')
  const [invite, setInvite] = useState<InviteType | null>(null)
  const [activeStep, setActiveStep] = useState(0)
  const [progress, setProgress] = useState(0)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [loading, setLoading] = useState<boolean>(false)
  const { emitter } = useMitt()

  useEffect(() => {
    !!invite?.video &&
      emitter.on('story-media-success', event => {
        if (!event?.data) return
        const { payload: data } = JSON.parse(event.data)
        if (data.id === invite!.id) {
          setLoading(false)
        }
      })
  }, [invite])

  const initialValuesStep4: {
    email: string
    emails: { email: string; name: string }[]
  } = {
    emails: [],
    email: ''
  }

  const userOrganization = user.organizations.length > 0 ? user.organizations[0] : null

  const initialValues: {
    collection: CollectionType | null
    instructions: string
    file: File | null
    recordingType: MediaType | 'NONE'
    expire: Date
    size: string
    title: string
    allowExpire: boolean
    allowOrganization: boolean
    callToActionInstructions: string
    callToActionButtonLabel: string
    callToAction: string
    hardStop: boolean
    template: boolean
    requestFiles: boolean
    requestImages: boolean
  } = {
    collection: initialCollection,
    instructions: '',
    file: null,
    recordingType: 'NONE',
    expire: addDays(new Date(), 60),
    size: '10',
    title: '',
    allowExpire: false,
    allowOrganization: !!userOrganization,
    callToActionInstructions: '',
    callToActionButtonLabel: '',
    callToAction: '',
    hardStop: false,
    template: false,
    requestFiles: false,
    requestImages: false
  }

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const generateLink = async (values: FormikValues) => {
    try {
      const inviteResult = await api.createInvite(
        {
          collectionId: values!.collection!.id,
          organizationId: values.allowOrganization && user.organizations.length > 0 ? user.organizations[0].id : null,
          title: values!.title,
          description: values!.instructions,
          dueAt: values.allowExpire ? values!.expire : null,
          requestedMediaLength: Number(values!.size),
          requestedMediaType: values.recordingType === 'NONE' ? null : values.recordingType,
          callToActionInstructions: values?.callToActionInstructions,
          callToActionButtonLabel: values?.callToActionButtonLabel,
          callToAction: values?.callToAction,
          hardStop: values?.hardStop,
          storyId: initialStory ? initialStory.id : null,
          template: values?.template,
          requestFiles: values?.requestFiles,
          requestImages: values?.requestImages
        },
        values.file,
        event => {
          setProgress(Math.round((event.loaded * 100) / event.total))
        }
      )
      setInvite(inviteResult)
      setProgress(0)
      setLoading(!!inviteResult.video)
      handleNext()
    } catch (e) {
      console.error(e)
    }
  }

  const sendEmails = async (values: FormikValues) => {
    try {
      if (values.emails.length) {
        const emailRecords = (values.emails as EmailRecords[]).map(value => ({
          email: value.email,
          name: value.name,
          jobTitle: value.jobTitle,
          functionalArea: value.functionalArea
        }))
        await api.sendInviteEmails(invite!.id, emailRecords, '', false)
        snackActions.open(`An email has been sent to recorders that are invited.`)
      }
      actions.close()
    } catch (e) {
      console.error(e)
    }
  }

  const handleChooseForm = (values: FormikValues) => {
    if (activeStep === 2) {
      generateLink(values)
    }
    if (activeStep === 3) {
      sendEmails(values)
    }
  }

  const formikStep4 = useFormik({
    initialValues: initialValuesStep4,
    validationSchema: schemaStep4,
    validateOnBlur: true,
    onSubmit: handleChooseForm
  })

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    validateOnBlur: true,
    onSubmit: handleChooseForm
  })

  const { isSubmitting, setFieldValue, touched, errors, handleReset } = formik

  useEffect(() => {
    setActiveStep(0)
    handleReset(initialValues)
  }, [])

  const steps = [
    { label: 'Choose collection', error: touched.collection && Boolean(errors.collection) },
    { label: 'Describe', error: touched.instructions && Boolean(errors.instructions) },
    { label: 'Configure', error: false },
    { label: 'Share', error: false }
  ]

  useEffect(() => {
    if (!selectedSuggestion) return
    setFieldValue('title', selectedSuggestion)
    setSelectedSuggestion('')
  }, [selectedSuggestion])
  return (
    <form>
      <Dialog
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            actions.close()
          }
        }}
        open
      >
        <DialogTitle>
          Request stories and invite recorders
          <IconButton
            aria-label="close"
            onClick={actions.close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ width: '100%', marginBottom: 2 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map(item => (
                <Step key={item.label}>
                  <StepLabel error={item.error}>{item.label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Box sx={{ width: '100%', marginY: 3, minHeight: 328 }}>
            <FormikProvider value={formik}>
              {activeStep === 0 && <Step1 allowSelectCollection={!initialValues.collection} />}
              {activeStep === 1 && <Step2 initialStory={initialStory} />}
              {activeStep === 2 && <Step3 progress={progress} />}
            </FormikProvider>
            <FormikProvider value={formikStep4}>
              {activeStep === 3 && <Step4 loading={loading} url={invite!.url} />}
            </FormikProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          {activeStep > 0 && activeStep < 3 && (
            <Button disabled={isSubmitting} onClick={handleBack} sx={{ mt: 1, mr: 1 }}>
              {'Back'}
            </Button>
          )}
          {activeStep === 3 && (
            <LoadingButton
              variant="contained"
              loading={formikStep4.isSubmitting}
              disabled={!formikStep4.isValid}
              onClick={() => formikStep4.handleSubmit()}
              sx={{ mt: 1, mr: 1 }}
            >
              Share and close
            </LoadingButton>
          )}
          {activeStep === 2 && (
            <LoadingButton
              variant="contained"
              loading={formik.isSubmitting}
              disabled={!formik.isValid}
              onClick={() => formik.handleSubmit()}
              sx={{ mt: 1, mr: 1 }}
            >
              Generate
            </LoadingButton>
          )}
          {activeStep < 2 && (
            <Button variant="contained" onClick={handleNext} sx={{ mt: 1, mr: 1 }}>
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </form>
  )
}

export default StepInviteRecorder
